import Decimal from 'decimal.js';
import { groupBy } from 'lodash';
import { UseFormSetError } from 'react-hook-form';

import {
  AugmentedCreateInsurancePolicyInput,
  AugmentedUpdateEntityInput,
  EntityKind,
  EntityStage,
  InsurancePolicyKind,
} from '@/types/schema';

import {
  LifeInsuranceStepFormShape,
  LifeInsuranceStepRowData,
} from './LifeInsuranceStep.types';

export function validateSelectedPolicies(
  policies: LifeInsuranceStepFormShape['policies'],
  setError?: UseFormSetError<LifeInsuranceStepFormShape>
): string | null {
  const selected = Object.entries(policies).filter(
    ([, row]) => row.shouldImport
  );
  let firstError: string | null = null;

  const addError = (
    policyId: string,
    field: keyof LifeInsuranceStepRowData,
    message: string
  ) => {
    firstError ??= message;
    setError?.(`policies.${policyId}.${field}`, { type: 'manual', message });
  };

  for (const [policyId, row] of selected) {
    if (!row.policyType) {
      addError(
        policyId,
        'policyType',
        'Please select a policy type for all selected policies.'
      );
    }
    if (!row.ownerEntityId) {
      addError(
        policyId,
        'ownerEntityId',
        'Please select an owner entity for all selected policies.'
      );
    }
    if (!row.deathBenefitAmount?.greaterThan(0)) {
      addError(
        policyId,
        'deathBenefitAmount',
        'Please enter a positive death benefit for all selected policies.'
      );
    }
    const policyHolderCount = new Set(row.policyHolderIDs.filter(Boolean)).size;
    if (policyHolderCount === 0) {
      addError(
        policyId,
        'policyHolderIDs',
        'Please select an insured person for all selected policies.'
      );
    } else if (isTermPolicy(row.policyType) && policyHolderCount !== 1) {
      addError(
        policyId,
        'policyHolderIDs',
        'Please select exactly one insured person for all selected term policies.'
      );
    }
    if (
      isTermPolicy(row.policyType) &&
      (row.termLength === '' || row.termLength <= 0)
    ) {
      addError(
        policyId,
        'termLength',
        'Please select a term length for all selected term policies.'
      );
    }
    if (isTermPolicy(row.policyType) && !row.policyStartDate) {
      addError(
        policyId,
        'policyStartDate',
        'Please select a policy start date for all selected term policies.'
      );
    }
  }

  return firstError;
}

export function hasSelectedPolicies(
  policies: LifeInsuranceStepFormShape['policies']
): boolean {
  return Object.values(policies).some((row) => row.shouldImport);
}

function isTermPolicy(policyType: InsurancePolicyKind | ''): boolean {
  return policyType === InsurancePolicyKind.Term;
}

function createPolicyInput(
  row: LifeInsuranceStepRowData,
  householdId: string
): AugmentedCreateInsurancePolicyInput {
  const isTerm = isTermPolicy(row.policyType);
  const policyHolderIDs = row.policyHolderIDs.filter(Boolean);

  return {
    create: {
      kind: row.policyType as InsurancePolicyKind,
      // deathBenefitAmount is required by the schema; default to 0 if not provided
      deathBenefitAmount: row.deathBenefitAmount ?? new Decimal(0),
      householdID: householdId,
      integrationInsuranceID: row.integrationInsuranceId,
      carrierName: row.insuranceCarrier || undefined,
      policyNumber: row.policyNumber || undefined,
      policyHolderIDs: policyHolderIDs.length ? policyHolderIDs : undefined,
      startDate: row.policyStartDate ?? undefined,
      notes: row.notes || undefined,
      premiumAmount: row.premiumAmount ?? undefined,
      premiumFrequency: row.premiumFrequency || undefined,
      initialPremiumDueDate: row.premiumInitialDueDate ?? undefined,

      // Term-only fields
      ...(isTerm && {
        termDurationYears: row.termLength !== '' ? row.termLength : undefined,
        conversionDate: row.isConvertible
          ? (row.conversionDate ?? undefined)
          : undefined,
      }),

      // Non-term fields
      ...(!isTerm && {
        survivorship: row.isConvertible,
        cashValue: row.cashValue ?? undefined,
        cashValueDate: row.cashValueDate ?? undefined,
        loanBalanceOutstanding: row.loanBalanceOutstanding ?? undefined,
      }),
    },
  };
}

/**
 * Maps an EntityKind to the corresponding subtype update field name on
 * AugmentedUpdateEntityInput. Each subtype's update input accepts a
 * `withPolicies` field for creating insurance policies on the entity.
 */
const ENTITY_KIND_TO_UPDATE_FIELD: Partial<
  Record<EntityKind, keyof AugmentedUpdateEntityInput>
> = {
  [EntityKind.InsurancePersonalAccount]: 'updateInsurancePersonalAccount',
  [EntityKind.IlitTrust]: 'updateIlitTrust',
  [EntityKind.SlatTrust]: 'updateSlatTrust',
  [EntityKind.CrtTrust]: 'updateCrtTrust',
  [EntityKind.CltTrust]: 'updateCltTrust',
  [EntityKind.IrrevocableTrust]: 'updateIrrevocableTrust',
  [EntityKind.RevocableTrust]: 'updateRevocableTrust',
  [EntityKind.DonorAdvisedFund]: 'updateDonorAdvisedFund',
  [EntityKind.PrivateFoundation]: 'updatePrivateFoundation',
  [EntityKind.CcorpBusinessEntity]: 'updateCcorpBusinessEntity',
  [EntityKind.ScorpBusinessEntity]: 'updateScorpBusinessEntity',
  [EntityKind.LlcBusinessEntity]: 'updateLlcBusinessEntity',
  [EntityKind.LpBusinessEntity]: 'updateLpBusinessEntity',
  [EntityKind.GpBusinessEntity]: 'updateGpBusinessEntity',
};

/**
 * Groups selected policies by owner entity and produces entity update mutations.
 * Uses the entity's kind to select the correct subtype update field, which
 * carries the `withPolicies` array for creating insurance policies.
 */
export function mapFormDataToMutations(
  formData: LifeInsuranceStepFormShape,
  householdId: string,
  entityKindById: Record<string, EntityKind>,
  entityStageById: Record<string, EntityStage>,
  subtypeIdByEntityId: Record<string, string>
): AugmentedUpdateEntityInput[] {
  const selected = Object.values(formData.policies).filter(
    (row) => row.shouldImport && row.policyType && row.ownerEntityId
  );

  const grouped = groupBy(selected, (row) => row.ownerEntityId);

  return Object.entries(grouped).map(([entityId, rows]) => {
    const withPolicies = rows.map((row) => createPolicyInput(row, householdId));
    const entityKind = entityKindById[entityId];
    const updateField = entityKind
      ? ENTITY_KIND_TO_UPDATE_FIELD[entityKind]
      : undefined;

    if (!updateField) {
      throw new Error(
        `Unsupported entity kind "${entityKind}" for entity ${entityId}`
      );
    }

    const subtypeId = subtypeIdByEntityId[entityId];
    if (!subtypeId) {
      throw new Error(`Missing subtype ID for entity ${entityId}`);
    }

    return {
      id: entityId,
      update:
        entityStageById[entityId] === EntityStage.PreCreated
          ? { stage: EntityStage.Active }
          : {},
      [updateField]: {
        id: subtypeId,
        update: {},
        withPolicies,
      },
    };
  });
}
